import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Month-over-month attribution comparison.
 * Compares the current calendar month against the same number of days of
 * the previous calendar month so the % is fair from day 1.
 */
export default function TrackingCompareWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: payload } = await api.get("/tracking/compare");
        setData(payload);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!data || (!data.currentTotal && !data.previousTotal)) return null;

  const monthLabel = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", year: "numeric" });

  return (
    <div className="border border-[#2A2A2A] p-6" data-testid="dashboard-mom-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-overline text-[#C9A55A]">Month-over-month · WhatsApp clicks</div>
          <div className="text-[10px] text-[#8A8A8A] mt-1">
            {monthLabel(data.currentMonth)} (first {data.daysElapsed}d) vs {monthLabel(data.previousMonth)} (same window)
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-[#F5F1EB] text-2xl" data-testid="mom-current-total">{data.currentTotal}</div>
          <Trend current={data.currentTotal} previous={data.previousTotal} />
        </div>
      </div>

      <div className="border-t border-[#2A2A2A]">
        {data.rows.length === 0 && (
          <div className="text-[#8A8A8A] text-sm py-6 text-center" data-testid="mom-empty">No channel data yet.</div>
        )}
        {data.rows.map((r) => (
          <div key={r.channel} className="grid grid-cols-12 items-center py-2 border-b border-[#2A2A2A] last:border-b-0 text-xs" data-testid={`mom-row-${r.channel.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="col-span-4 text-[#F5F1EB] truncate">{r.channel}</div>
            <div className="col-span-2 text-right text-[#F5F1EB]">{r.current}</div>
            <div className="col-span-2 text-right text-[#8A8A8A]">prev: {r.previous}</div>
            <div className="col-span-4 text-right">
              <Trend current={r.current} previous={r.previous} pct={r.pct} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Trend({ current, previous, pct }) {
  const delta = current - previous;
  let pctVal = pct;
  if (pctVal === undefined) {
    pctVal = previous > 0 ? Math.round(((current - previous) / previous) * 100 * 10) / 10 : null;
  }
  if (delta === 0 || (pctVal === null && current === 0)) {
    return (
      <span className="inline-flex items-center gap-1 text-[#8A8A8A] text-[11px] tracking-[0.18em] uppercase">
        <Minus size={12} /> 0%
      </span>
    );
  }
  const up = delta > 0;
  const color = up ? "#34A853" : "#B8342A";
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase" style={{ color }}>
      <Icon size={12} /> {pctVal === null ? "new" : `${up ? "+" : ""}${pctVal}%`}
    </span>
  );
}
